import { useMemo, useRef, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { evaluateBranching, realizePath } from '../../../lib/surveyBranching';
import type { QuestionStep, SurveyStep } from '../../../types/survey';
import type { PublicSurveyDto } from '../../../types/surveyPublic';
import type { SurveyAnswer, SurveyAnswerValue } from '../../../types/surveyResponse';
import { postAnswer, postSubmit } from './publicApi';
import { ContentPageView, StepInput, defaultDraft } from './StepInputs';
import { OutroView } from './OutroView';

// Phase 6B D4 — the interactive runner. Server-authoritative (validation +
// per_recipient persistence + F3 truncation via /answer) with client-side
// optimistic navigation using the shared engine (evaluateBranching). One step
// per page, mobile-first. per_location holds answers in memory + submits the
// batch (Q3); per_recipient persists each answer + resumes.

function isEmpty(v: SurveyAnswerValue): boolean {
  switch (v.type) {
    case 'yes_no':
      return typeof v.value !== 'boolean';
    case 'multiple_choice_single':
      return v.optionId === '';
    case 'multiple_choice_multi':
      return v.optionIds.length === 0;
    case 'short_text':
    case 'long_text':
      return v.text.trim() === '';
    case 'rating_scale':
      return Number.isNaN(v.rating);
    case 'number':
      return Number.isNaN(v.number);
    case 'date':
      return v.date === '';
    case 'name':
      return v.name.trim() === '';
  }
}

function upsert(answers: SurveyAnswer[], next: SurveyAnswer): SurveyAnswer[] {
  const i = answers.findIndex((a) => a.stepId === next.stepId);
  if (i === -1) return [...answers, next];
  const copy = answers.slice();
  copy[i] = next;
  return copy;
}

export function SurveyRunner({ dto, token }: { dto: PublicSurveyDto; token: string }) {
  const steps = dto.composition.steps;
  const questionSteps = useMemo(() => steps.filter((s) => s.kind === 'question'), [steps]);
  const indexById = useMemo(() => new Map(steps.map((s, i) => [s.id, i] as const)), [steps]);

  const [answers, setAnswers] = useState<SurveyAnswer[]>(dto.answers ?? []);
  const [currentStepId, setCurrentStepId] = useState<string>(steps[0]?.id ?? '');
  const [history, setHistory] = useState<string[]>([]);
  const [draft, setDraft] = useState<SurveyAnswerValue | null>(null);
  const [draftStepId, setDraftStepId] = useState<string | null>(null);
  const [comment, setComment] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncationCount, setTruncationCount] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const pendingProceed = useRef<(() => Promise<void>) | null>(null);

  const step = steps.find((s) => s.id === currentStepId) ?? null;

  // Seed the draft when the step changes (resume / back show the prior answer).
  if (step && step.kind === 'question' && draftStepId !== step.id) {
    const existing = answers.find((a) => a.stepId === step.id);
    setDraftStepId(step.id);
    setDraft(existing ? existing.value : defaultDraft(step));
    setComment(existing?.comment ?? '');
    setError(null);
  }

  if (submitted || !step) return <OutroView outro={dto.composition.outroScreen} />;

  const stepAfter = (s: SurveyStep, value: SurveyAnswerValue | null): string | null => {
    const idx = indexById.get(s.id) ?? -1;
    if (s.kind === 'content' || value === null) return steps[idx + 1]?.id ?? null;
    const outcome = evaluateBranching(s as QuestionStep, value);
    if (outcome.kind === 'skip_to_outro') return null;
    if (outcome.kind === 'go_to') return outcome.targetStepId;
    return steps[idx + 1]?.id ?? null;
  };

  const finalize = async (finalAnswers: SurveyAnswer[]) => {
    setBusy(true);
    setError(null);
    try {
      if (dto.linkMode === 'per_location') {
        const nameAnswer = finalAnswers.find((a) => a.value.type === 'name');
        await postSubmit(token, {
          answers: finalAnswers.map((a) => ({ stepId: a.stepId, value: a.value, comment: a.comment })),
          respondentName: nameAnswer?.value.type === 'name' ? nameAnswer.value.name : null,
        });
      } else {
        await postSubmit(token, {});
      }
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const goTo = (s: SurveyStep, value: SurveyAnswerValue | null, nextAnswers: SurveyAnswer[]) => {
    const nextId = stepAfter(s, value);
    if (nextId === null) {
      void finalize(nextAnswers);
      return;
    }
    setHistory((h) => [...h, s.id]);
    setCurrentStepId(nextId);
  };

  const onNext = () => {
    if (step.kind === 'content') {
      goTo(step, null, answers);
      return;
    }
    const value = draft;
    if (!value) return;
    if (step.required && isEmpty(value)) {
      setError('This question is required.');
      return;
    }
    const answer: SurveyAnswer = {
      stepId: step.id,
      promptSnapshot: step.prompt,
      questionTypeSnapshot: step.type,
      value,
      comment: comment.trim() === '' ? null : comment,
      answeredAt: new Date().toISOString(),
    };
    const merged = upsert(answers, answer);
    const validIds = new Set(realizePath(steps, merged).validAnswers.map((a) => a.stepId));
    const truncated = answers.filter((a) => a.stepId !== step.id && !validIds.has(a.stepId));
    const nextAnswers = merged.filter((a) => a.stepId === step.id || validIds.has(a.stepId));

    const proceed = async () => {
      setBusy(true);
      setError(null);
      try {
        const res = await postAnswer(token, { stepId: step.id, value, comment: answer.comment, confirmTruncation: truncated.length > 0 });
        if (res.needsTruncationConfirm) {
          await postAnswer(token, { stepId: step.id, value, comment: answer.comment, confirmTruncation: true });
        }
        setAnswers(nextAnswers);
        goTo(step, value, nextAnswers);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not save your answer.');
      } finally {
        setBusy(false);
      }
    };

    pendingProceed.current = proceed;
    if (truncated.length > 0) setTruncationCount(truncated.length);
    else void proceed();
  };

  const onBack = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1]!;
    setHistory((h) => h.slice(0, -1));
    setCurrentStepId(prev);
  };

  const qIndex = step.kind === 'question' ? questionSteps.findIndex((s) => s.id === step.id) + 1 : 0;
  const qTotal = questionSteps.length;
  const allowComment =
    step.kind === 'question' && step.config.type === 'rating_scale' && step.config.allowOptionalComment === true;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      {step.kind === 'question' && (
        <div className="mb-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Question {qIndex} of {qTotal}
          </p>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-kyck-500" style={{ width: `${qTotal ? (qIndex / qTotal) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      {step.kind === 'content' ? (
        <ContentPageView step={step} />
      ) : (
        <>
          <h2 className="text-lg font-semibold text-slate-900">{step.prompt}</h2>
          {step.description && <p className="mt-1 text-sm text-slate-500">{step.description}</p>}
          <div className="mt-4">{draft && <StepInput step={step} value={draft} onChange={setDraft} />}</div>
          {allowComment && (
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment (optional)"
              rows={2}
              className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
            />
          )}
        </>
      )}

      {error && <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="mt-6 flex items-center justify-between">
        <Button variant="outline" onClick={onBack} disabled={history.length === 0 || busy}>
          Back
        </Button>
        <Button onClick={onNext} disabled={busy}>
          {busy ? 'Saving…' : step.kind === 'content' ? step.ctaLabel || 'Next' : 'Next'}
        </Button>
      </div>

      {truncationCount !== null && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4" onClick={() => setTruncationCount(null)}>
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-800">Change this answer?</h3>
            <p className="mt-2 text-sm text-slate-600">
              Changing this will reset your {truncationCount} later answer{truncationCount === 1 ? '' : 's'}. Continue?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTruncationCount(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setTruncationCount(null);
                  void pendingProceed.current?.();
                }}
                disabled={busy}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
